import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, Tag, message, Tooltip, Popconfirm, Progress, Popover, Descriptions, Alert } from 'antd';
import { PlayCircleOutlined, StopOutlined, DeleteOutlined, ReloadOutlined, ClearOutlined, LoadingOutlined } from '@ant-design/icons';

/**
 * DAS-1703: Prepared Runs Table
 * Displays queued configurations for serial run execution
 */
const PreparedRunsTable = ({ callAction, onViewConfiguration, isSerialRunActive, setIsSerialRunActive }) => {
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [startingBatch, setStartingBatch] = useState(false);
  const [stoppingBatch, setStoppingBatch] = useState(false);
  const pollRef = useRef(null);

  // Fetch batch items
  const fetchBatchItems = async () => {
    setLoading(true);
    try {
      const response = await callAction('list_batch_items', {}, { useLoader: false });
      if (response?.success && response?.data) {
        setBatch(response.data.batch);
        setItems(response.data.items || []);
        
        // Update serial run active state
        const isRunning = response.data.batch?.status === 'running';
        if (setIsSerialRunActive) {
          setIsSerialRunActive(isRunning);
        }
      }
    } catch (error) {
      message.error(`Error fetching batch: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchItems();
    
    // Poll while running
    pollRef.current = setInterval(() => {
      fetchBatchItems();
    }, 3000);
    
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  // Start serial run
  const handleStartBatch = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Select at least one item to run');
      return;
    }
    
    setStartingBatch(true);
    try {
      const response = await callAction('start_batch', { 
        selected_item_ids: selectedRowKeys 
      });
      if (response?.success) {
        message.success(`Serial run started with ${response.data.total_runs} configurations`);
        setSelectedRowKeys([]);
        fetchBatchItems();
      } else {
        message.error(response?.error || 'Failed to start serial run');
      }
    } catch (error) {
      message.error(`Error starting batch: ${error.message}`);
    } finally {
      setStartingBatch(false);
    }
  };

  // Stop serial run
  const handleStopBatch = async () => {
    setStoppingBatch(true);
    try {
      const response = await callAction('stop_batch', {});
      if (response?.success) {
        message.info(`Serial run stopped. ${response.data.skipped_count} items skipped.`);
        fetchBatchItems();
      } else {
        message.error(response?.error || 'Failed to stop serial run');
      }
    } catch (error) {
      message.error(`Error stopping batch: ${error.message}`);
    } finally {
      setStoppingBatch(false);
    }
  };

  // Remove item from queue
  const handleRemoveItem = async (itemId) => {
    try {
      const response = await callAction('remove_batch_item', { item_id: itemId });
      if (response?.success) {
        message.success('Removed from queue');
        fetchBatchItems();
      } else {
        message.error(response?.error || 'Failed to remove item');
      }
    } catch (error) {
      message.error(`Error removing item: ${error.message}`);
    }
  };

  // Clear all pending items
  const handleClearBatch = async () => {
    try {
      const response = await callAction('clear_batch', {});
      if (response?.success) {
        message.success(`Cleared ${response.data.deleted_count} items from queue`);
        setSelectedRowKeys([]);
        fetchBatchItems();
      } else {
        message.error(response?.error || 'Failed to clear batch');
      }
    } catch (error) {
      message.error(`Error clearing batch: ${error.message}`);
    }
  };

  // Clear all completed/finished items
  const handleClearCompleted = async () => {
    const completedItems = items.filter(i => ['completed', 'failed', 'skipped'].includes(i.item_status));
    if (completedItems.length === 0) return;
    
    try {
      let deletedCount = 0;
      for (const item of completedItems) {
        const response = await callAction('remove_batch_item', { item_id: item.item_id }, { useLoader: false });
        if (response?.success) deletedCount++;
      }
      message.success(`Cleared ${deletedCount} completed items`);
      fetchBatchItems();
    } catch (error) {
      message.error(`Error clearing completed items: ${error.message}`);
    }
  };

  // Status icon helper
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'running': return '🔄';
      case 'completed': return '✅';
      case 'failed': return '❌';
      case 'skipped': return '⏭️';
      default: return '❓';
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'order_index',
      key: 'order_index',
      width: 50,
      render: (idx) => <strong>{idx + 1}</strong>
    },
    {
      title: 'Status',
      dataIndex: 'item_status',
      key: 'item_status',
      width: 100,
      render: (status) => {
        const icon = getStatusIcon(status);
        let color = 'default';
        if (status === 'completed') color = 'green';
        else if (status === 'running') color = 'blue';
        else if (status === 'failed') color = 'red';
        else if (status === 'skipped') color = 'orange';
        
        return (
          <Space>
            <span>{icon}</span>
            <Tag color={color}>{status?.toUpperCase()}</Tag>
          </Space>
        );
      }
    },
    {
      title: 'Progress',
      dataIndex: 'progress_pct',
      key: 'progress_pct',
      width: 100,
      render: (pct, record) => {
        if (record.item_status === 'pending') {
          return <span style={{ color: '#999' }}>—</span>;
        }
        if (record.item_status === 'completed') {
          return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>100%</span>;
        }
        if (record.item_status === 'failed' || record.item_status === 'skipped') {
          return <span style={{ color: '#999' }}>—</span>;
        }
        // Running
        if (pct != null) {
          return (
            <Progress 
              percent={Number(pct).toFixed(1)} 
              size="small" 
              status="active"
              style={{ width: 80 }}
            />
          );
        }
        return <span style={{ color: '#999' }}>0%</span>;
      }
    },
    {
      title: 'Run ID',
      dataIndex: 'run_id',
      key: 'run_id',
      width: 80,
      render: (id) => id ? <strong>#{id}</strong> : <span style={{ color: '#999' }}>-</span>
    },
    {
      title: 'Conf ID',
      dataIndex: 'configuration_id',
      key: 'configuration_id',
      width: 80,
      render: (configId) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => onViewConfiguration && onViewConfiguration(configId)}
          disabled={!onViewConfiguration}
        >
          #{configId}
        </Button>
      )
    },
    {
      // DAS-1726: Comment column
      title: 'Comment',
      dataIndex: 'user_comment',
      key: 'user_comment',
      width: 100,
      render: (comment) => {
        if (!comment) return <span style={{ color: '#999' }}>-</span>;
        const displayText = comment.length > 10 ? comment.substring(0, 10) + '...' : comment;
        return (
          <Tooltip title={comment}>
            <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{displayText}</span>
          </Tooltip>
        );
      }
    },
    {
      // DAS-1710: Min Market Cap column
      title: <Tooltip title="Minimum market cap filter">Min Cap</Tooltip>,
      dataIndex: 'min_market_cap_eur',
      key: 'min_market_cap_eur',
      width: 80,
      align: 'right',
      render: (val) => {
        if (!val) return <span style={{ color: '#999' }}>-</span>;
        const billions = val / 1000000000;
        return <span style={{ fontFamily: 'monospace' }}>€{billions.toFixed(0)}B</span>;
      }
    },
    {
      title: 'Rebalancing',
      dataIndex: 'rebalancing_label',
      key: 'rebalancing_label',
      width: 140,
      render: (label, record) => {
        const cfg = record.rebalancing_config;
        if (!cfg || !cfg.enabled) {
          return <Tag color="default">-</Tag>;
        }
        
        const factor = cfg.factor_strategy || {};
        const presetLabel = factor.preset === 'value' ? 'Value' : factor.preset === 'growth' ? 'Growth' : 'Custom';
        
        const popoverContent = (
          <div style={{ width: 320 }}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Frequency">
                {cfg.frequency_days} days
              </Descriptions.Item>
              <Descriptions.Item label="Selection percentile">
                {cfg.selection_percentile}%
              </Descriptions.Item>
              <Descriptions.Item label="Sector-neutral">
                <Tag color={cfg.sector_neutral ? 'green' : 'orange'}>
                  {cfg.sector_neutral ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Factor Strategy">
                <Tag color="purple">{presetLabel}</Tag>
              </Descriptions.Item>
            </Descriptions>
          </div>
        );
        
        return (
          <Popover content={popoverContent} title="Rebalancing Config" trigger="hover">
            <Tag color="blue" style={{ fontFamily: 'monospace', fontSize: 11, cursor: 'pointer' }}>
              {label || '-'}
            </Tag>
          </Popover>
        );
      }
    },
    {
      title: 'Portfolio',
      dataIndex: 'portfolio_name',
      key: 'portfolio_name',
      ellipsis: true,
      render: (name) => name || '-'
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => {
        if (!record.start_date || !record.end_date) return '-';
        const start = new Date(record.start_date).toLocaleDateString();
        const end = new Date(record.end_date).toLocaleDateString();
        return `${start} → ${end}`;
      }
    },
    {
      // DAS-1770: Result% (TWR) column - same as Run History
      title: 'Result%',
      dataIndex: 'result_twr_pct',
      key: 'result_twr_pct',
      width: 90,
      render: (val) => {
        if (val == null) return <span style={{ color: '#999' }}>-</span>;
        const num = Number(val);
        const color = num >= 0 ? '#52c41a' : '#cf1322';
        const sign = num >= 0 ? '+' : '';
        return <span style={{ color, fontWeight: 'bold' }}>{sign}{num.toFixed(2)}%</span>;
      }
    },
    {
      title: 'Excess vs B&H',
      dataIndex: 'excess_twr_pct',
      key: 'excess_twr_pct',
      width: 110,
      render: (val) => {
        if (val == null) return <span style={{ color: '#999' }}>-</span>;
        const num = Number(val);
        const color = num >= 0 ? '#52c41a' : '#cf1322';
        const sign = num >= 0 ? '+' : '';
        return <span style={{ color, fontWeight: 'bold' }}>{sign}{num.toFixed(2)}%</span>;
      }
    },
    {
      title: 'Excess vs 2nd BM',
      dataIndex: 'secondary_excess_twr_pct',
      key: 'secondary_excess_twr_pct',
      width: 130,
      render: (val, record) => {
        if (val == null) return <span style={{ color: '#999' }}>-</span>;
        const num = Number(val);
        const color = num >= 0 ? '#52c41a' : '#cf1322';
        const sign = num >= 0 ? '+' : '';
        return (
          <div>
            <span style={{ color, fontWeight: 'bold' }}>{sign}{num.toFixed(2)}%</span>
            {record.secondary_benchmark && (
              <div style={{ fontSize: '10px', color: '#888' }}>{record.secondary_benchmark}</div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => {
        // Can't delete while running
        if (record.item_status === 'running') {
          return null;
        }
        return (
          <Popconfirm
            title={`Delete item #${record.order_index + 1}?`}
            description={record.item_status === 'completed' ? 'This will remove it from this list (the run data remains in Run History)' : undefined}
            onConfirm={() => handleRemoveItem(record.item_id)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={batch?.status === 'running' && record.item_status === 'pending'}
            />
          </Popconfirm>
        );
      }
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      disabled: record.item_status !== 'pending' || batch?.status === 'running'
    })
  };

  const isRunning = batch?.status === 'running';
  const pendingCount = items.filter(i => i.item_status === 'pending').length;
  const completedCount = items.filter(i => i.item_status === 'completed').length;
  const finishedCount = items.filter(i => ['completed', 'failed', 'skipped'].includes(i.item_status)).length;

  return (
    <div>
      {/* Status Banner */}
      {isRunning && (
        <Alert
          type="info"
          showIcon
          icon={<LoadingOutlined spin />}
          message={
            <Space>
              <strong>Serial Run in Progress</strong>
              <span>({batch?.completed_runs || 0}/{batch?.total_runs || 0} complete)</span>
            </Space>
          }
          style={{ marginBottom: 16 }}
          action={
            <Button 
              danger 
              size="small" 
              icon={<StopOutlined />}
              loading={stoppingBatch}
              onClick={handleStopBatch}
            >
              Stop
            </Button>
          }
        />
      )}

      {/* Control Bar */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <strong>Prepared Runs</strong>
          <span style={{ marginLeft: 8, color: '#666' }}>
            ({pendingCount} pending, {completedCount} completed)
          </span>
        </div>
        <Space>
          {!isRunning && pendingCount > 0 && (
            <>
              <Tooltip title={selectedRowKeys.length === 0 ? "Select items to run" : `Start ${selectedRowKeys.length} selected runs`}>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={handleStartBatch}
                  loading={startingBatch}
                  disabled={selectedRowKeys.length === 0}
                >
                  Start Serial Run ({selectedRowKeys.length})
                </Button>
              </Tooltip>
              <Popconfirm
                title="Clear all pending items?"
                onConfirm={handleClearBatch}
                okText="Clear"
                cancelText="Cancel"
              >
                <Button icon={<ClearOutlined />} danger>
                  Clear Queue
                </Button>
              </Popconfirm>
            </>
          )}
          {!isRunning && finishedCount > 0 && (
            <Popconfirm
              title={`Clear ${finishedCount} finished items?`}
              description="This removes completed/failed/skipped items from this list"
              onConfirm={handleClearCompleted}
              okText="Clear"
              cancelText="Cancel"
            >
              <Button icon={<DeleteOutlined />}>
                Clear Finished ({finishedCount})
              </Button>
            </Popconfirm>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchBatchItems}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Table */}
      <Table
        rowKey="item_id"
        loading={loading}
        columns={columns}
        dataSource={items}
        rowSelection={!isRunning ? rowSelection : undefined}
        pagination={false}
        size="small"
        scroll={{ x: 1200 }}
        locale={{
          emptyText: (
            <div style={{ padding: 40, color: '#666' }}>
              <p>No configurations queued for serial run.</p>
              <p style={{ fontSize: 12 }}>
                Open the Configuration drawer, save a config, then click "Queue for Serial".
              </p>
            </div>
          )
        }}
      />
    </div>
  );
};

export default PreparedRunsTable;

